import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import {
  api,
  AUTH_CHANNEL_NAME,
  AUTH_SESSION_EXPIRED_EVENT,
  AUTH_SYNC_STORAGE_KEY,
  clearCsrfToken,
  clearLogoutIntent,
  extractUser,
  formatApiError,
  logoutBrowserSession,
} from "@/lib/api";
import AuthOverlay from "@/components/AuthOverlay";
import LogoutOverlay from "@/components/LogoutOverlay";

const AuthContext = createContext(null);
const LEGACY_AUTH_KEYS = ["access_token", "refresh_token", "token", "current_user"];
let legacyMigrationPromise = null;

function asSessionUser(data) {
  const normalized = extractUser(data);
  if (!normalized || normalized.anonymous === true || (!normalized.id && !normalized.user_id)) {
    return null;
  }
  return normalized;
}

function removeLegacyAuthStorage() {
  if (typeof window === "undefined") return;
  try {
    LEGACY_AUTH_KEYS.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Storage may be disabled by browser privacy settings. Cookie auth still works.
  }
}

function migrateLegacySession() {
  if (typeof window === "undefined") return Promise.resolve();
  if (legacyMigrationPromise) return legacyMigrationPromise;

  legacyMigrationPromise = (async () => {
    let legacyToken = null;
    try {
      // Older deployments used the same opaque session value for access and
      // refresh. Prefer a dedicated refresh value if one exists.
      legacyToken = window.localStorage.getItem("refresh_token") ||
        window.localStorage.getItem("access_token") ||
        window.localStorage.getItem("token");
    } catch {
      // Continue to the cleanup below when browser storage cannot be read.
    }

    try {
      if (legacyToken) {
        let legacyExchangeIsSafe = false;
        try {
          // Never let a stale localStorage value replace a newer cookie session
          // (including one restored by the normal cookie refresh interceptor).
          // A 200 anonymous response or a definitive 401 proves there is no
          // usable cookie identity and makes the one-time exchange safe.
          const { data } = await api.get("/auth/me", {
            headers: { "Cache-Control": "no-cache" },
          });
          legacyExchangeIsSafe = !asSessionUser(data);
        } catch (error) {
          legacyExchangeIsSafe = error?.response?.status === 401;
        }

        if (legacyExchangeIsSafe) {
          // This is the only code path that reads a persisted bearer credential.
          // The backend atomically exchanges legacy sessions for rotated cookies.
          await api.post("/auth/refresh", null, {
            headers: { Authorization: `Bearer ${legacyToken}` },
            skipAuthRefresh: true,
          });
        }
      }
    } catch {
      // An expired/revoked legacy session simply becomes a signed-out session.
    } finally {
      legacyToken = null;
      removeLegacyAuthStorage();
    }
  })().finally(() => { legacyMigrationPromise = null; });

  return legacyMigrationPromise;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authOverlay, setAuthOverlay] = useState({ active: false, type: "login", step: 1 });
  const authChannelRef = useRef(null);
  const authRequestRef = useRef(0);
  const logoutAttemptRef = useRef(false);

  // const triggerAuthOverlay = useCallback((type) => {
  //   setAuthOverlay({ active: true, type, step: 1 });
  //   const stepTimer = setTimeout(() => {
  //     setAuthOverlay((prev) => ({ ...prev, step: 2 }));
  //   }, 450);

  //   const finish = async (minMs = 850) => {
  //     const start = Date.now();
  //     await new Promise((r) => setTimeout(r, Math.max(0, minMs - (Date.now() - start))));
  //     clearTimeout(stepTimer);
  //     setAuthOverlay({ active: false, type: "login", step: 1 });
  //   };

  //   const cancel = () => {
  //     clearTimeout(stepTimer);
  //     setAuthOverlay({ active: false, type: "login", step: 1 });
  //   };

  //   return { finish, cancel };
  // }, []);
const triggerAuthOverlay = useCallback((type) => {
  const startedAt = Date.now();

  setAuthOverlay({
    active: true,
    type,
    step: 1,
  });

  const finish = async (minimumTotalMs = 850) => {
    setAuthOverlay((previous) => ({
      ...previous,
      step: 2,
    }));

    const elapsed = Date.now() - startedAt;

    // Show the success step for at least 350 ms.
    const remaining = Math.max(
      350,
      minimumTotalMs - elapsed
    );

    await new Promise((resolve) =>
      setTimeout(resolve, remaining)
    );

    setAuthOverlay({
      active: false,
      type: "login",
      step: 1,
    });
  };

  const cancel = () => {
    setAuthOverlay({
      active: false,
      type: "login",
      step: 1,
    });
  };

  return {
    finish,
    cancel,
  };
}, []);
  const clearSession = useCallback(() => {
    authRequestRef.current += 1;
    setUser(null);
    setPermissions([]);
    setLoading(false);
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const { data } = await api.get("/me/permissions");
      return Array.isArray(data?.permissions) ? data.permissions : [];
    } catch {
      return [];
    }
  }, []);

  const readSessionUser = useCallback(async () => {
    const { data } = await api.get("/auth/me", {
      headers: { "Cache-Control": "no-cache" },
    });
    return asSessionUser(data);
  }, []);

  const activateSession = useCallback(async () => {
    // Verify the cookie round-trip instead of trusting user data returned by a
    // login endpoint. This catches rejected cookies/CORS configuration early.
    const requestId = ++authRequestRef.current;
    const normalized = await readSessionUser();
    if (!normalized) throw new Error("The browser session could not be established. Please try again.");
    const nextPermissions = await fetchPermissions();
    if (requestId !== authRequestRef.current) {
      throw new Error("The browser session changed while signing in. Please try again.");
    }
    setUser(normalized);
    setPermissions(nextPermissions);
    return normalized;
  }, [fetchPermissions, readSessionUser]);

  const checkAuth = useCallback(async () => {
    if (logoutAttemptRef.current) return null;
    const requestId = ++authRequestRef.current;
    try {
      const normalized = await readSessionUser();
      if (requestId !== authRequestRef.current) return null;
      if (!normalized) {
        clearSession();
        return null;
      }
      const nextPermissions = await fetchPermissions();
      if (requestId !== authRequestRef.current) return null;
      setUser(normalized);
      setPermissions(nextPermissions);
      return normalized;
    } catch (error) {
      // Keep an already-rendered session during a transient network/server
      // outage. A 401 (or the API client's definitive-expiry event) is the only
      // error that proves the browser session itself is no longer valid.
      if (error?.response?.status === 401) clearSession();
      return null;
    } finally {
      if (requestId === authRequestRef.current) setLoading(false);
    }
  }, [clearSession, fetchPermissions, readSessionUser]);

  const publishSessionChange = useCallback(() => {
    // Broadcast only an event name. No credential or user/PII is persisted or
    // sent between tabs; receiving tabs re-read their httpOnly cookie session.
    if (typeof window === "undefined") return;
    try { authChannelRef.current?.postMessage({ type: "session-changed" }); } catch {}
    try {
      window.localStorage.setItem(AUTH_SYNC_STORAGE_KEY, String(Date.now()));
      window.localStorage.removeItem(AUTH_SYNC_STORAGE_KEY);
    } catch {}
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      await migrateLegacySession();
      if (!active) return;
      await checkAuth();
    })();
    return () => { active = false; };
  }, [checkAuth]);

  useEffect(() => {
    const onExpired = () => clearSession();
    const onFocus = () => checkAuth();
    const onVisibility = () => {
      if (document.visibilityState === "visible") checkAuth();
    };
    const onStorage = (event) => {
      if (event.key === AUTH_SYNC_STORAGE_KEY) checkAuth();
    };

    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, onExpired);
    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisibility);

    if (typeof window.BroadcastChannel === "function") {
      const channel = new window.BroadcastChannel(AUTH_CHANNEL_NAME);
      channel.onmessage = (event) => {
        if (event?.data?.type === "session-changed") checkAuth();
      };
      authChannelRef.current = channel;
    }

    return () => {
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, onExpired);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisibility);
      authChannelRef.current?.close();
      authChannelRef.current = null;
    };
  }, [checkAuth, clearSession]);

  const login = async (email, password, role) => {
    const overlay = triggerAuthOverlay("login");
    try {
      clearLogoutIntent();
      // role is the sign-in tab (buyer|agent). The backend validates it against
      // the roles the account holds and stores the active role server-side.
      await api.post("/auth/login", role ? { email, password, role } : { email, password });
      const normalized = await activateSession();
      publishSessionChange();
      await overlay.finish(850);
      return { ok: true, user: normalized };
    } catch (e) {
      overlay.cancel();
      return { ok: false, error: formatApiError(e) };
    }
  };

  const loginWithGoogle = async (credential) => {
    const overlay = triggerAuthOverlay("login");
    try {
      clearLogoutIntent();
      await api.post("/auth/google/verify", { credential });
      const normalized = await activateSession();
      publishSessionChange();
      await overlay.finish(850);
      return { ok: true, user: normalized };
    } catch (e) {
      overlay.cancel();
      return { ok: false, error: formatApiError(e) };
    }
  };

  const register = async (payload) => {
    const overlay = triggerAuthOverlay("register");
    try {
      const { data } = await api.post("/auth/register", payload);
      await overlay.finish(850);
      if (data?.requires_verification) {
        return { ok: true, requires_verification: true, email: data.email };
      }
      // Registration creates the account but deliberately does not create a
      // browser session. OTP verification followed by login establishes it.
      return { ok: true, user: asSessionUser(data) };
    } catch (e) {
      overlay.cancel();
      return { ok: false, error: formatApiError(e) };
    }
  };

  const verifyOtp = async (email, otp) => {
    try {
      await api.post("/auth/verify-otp", { email, otp });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    if (logoutAttemptRef.current || authOverlay.active) {
      return { ok: true };
    }
    logoutAttemptRef.current = true;
    const overlay = triggerAuthOverlay("logout");

    try {
      await logoutBrowserSession();
      clearCsrfToken();
      removeLegacyAuthStorage();
      clearSession();

      try {
        if (typeof window !== "undefined") {
          window.localStorage.removeItem("recently_viewed_properties");
          window.localStorage.removeItem("shreehomes_read_notifications");
          window.sessionStorage.clear();
        }
      } catch {
        // Ignore storage exceptions if browser storage is blocked
      }

      publishSessionChange();
      await overlay.finish(850);
      return { ok: true };
    } catch (e) {
      if (e?.response?.status === 401) {
        clearCsrfToken();
        removeLegacyAuthStorage();
        clearSession();
        publishSessionChange();
        await overlay.finish(850);
        return { ok: true };
      }
      overlay.cancel();
      return { ok: false, error: formatApiError(e) };
    } finally {
      logoutAttemptRef.current = false;
    }
  };

  const refreshUser = async () => {
    if (logoutAttemptRef.current) return null;
    const requestId = ++authRequestRef.current;
    try {
      const normalized = await readSessionUser();
      if (requestId !== authRequestRef.current) return null;
      if (!normalized) {
        clearSession();
        return null;
      }
      const nextPermissions = await fetchPermissions();
      if (requestId !== authRequestRef.current) return null;
      setUser(normalized);
      setPermissions(nextPermissions);
      return normalized;
    } catch (error) {
      if (error?.response?.status === 401) clearSession();
      return null;
    }
  };

  const can = useCallback((permission) => permissions.includes(permission), [permissions]);

  return (
    <AuthContext.Provider value={{
      user,
      permissions,
      can,
      loading,
      isLoggingOut: authOverlay.active && authOverlay.type === "logout",
      isAuthProcessing: authOverlay.active,
      logoutStep: authOverlay.step,
      authOverlay,
      login,
      loginWithGoogle,
      register,
      verifyOtp,
      logout,
      refreshUser,
    }}>
      {children}
      <AuthOverlay active={authOverlay.active} type={authOverlay.type} step={authOverlay.step} />
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
